import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import MayaMenubar from './MayaMenubar'
import SidebarOutliner from './SidebarOutliner'
import WelcomeScreen from './WelcomeScreen'
import { featuredProjects } from '../data/portfolio'
import {
  hasTransitionKind,
  subscribeTransitionCoordinator,
  TRANSITION_KINDS,
} from '../lib/transitionCoordinator'

const sceneNameFromPath = (pathname) => {
  if (pathname === '/contact') return 'contact'
  if (pathname === '/about') return 'about'
  if (pathname.startsWith('/projects/')) {
    const id = pathname.split('/')[2]
    const project = featuredProjects.find((item) => item.id === id)
    return project?.title?.toLowerCase() || 'project'
  }
  if (pathname.startsWith('/projects')) return 'projects'
  return 'showreel'
}

const MayaLogo = () => (
  <img
    src="/images/maya/darrenico.png"
    alt=""
    className="maya-logo h-4 w-4 object-contain"
    aria-hidden="true"
  />
)

const FlatShell = () => {
  const location = useLocation()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const shellRef = useRef(null)
  const headerRef = useRef(null)

  useLayoutEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  useEffect(() => {
    let slideWasActive = hasTransitionKind(TRANSITION_KINDS.slide)
    return subscribeTransitionCoordinator((snap) => {
      const slideActive = snap.activeKinds.includes(TRANSITION_KINDS.slide)
      if (slideWasActive && !slideActive) {
        window.scrollTo(0, 0)
      }
      slideWasActive = slideActive
    })
  }, [])

  useEffect(() => {
    setDrawerOpen(false)
  }, [location.pathname])

  useLayoutEffect(() => {
    const shell = shellRef.current
    const header = headerRef.current
    if (!shell || !header) return undefined

    const writeTreeHeight = () => {
      const headerH = [
        '.flat-titlebar',
        '.maya-chrome--flat',
        '.flat-tabbar',
        '.flat-tabbar-edge',
      ].reduce((sum, selector) => {
        const node = header.querySelector(selector)
        return sum + (node ? node.getBoundingClientRect().height : 0)
      }, 0)
      const search = header.querySelector('.maya-outliner-search-wrap')
      const searchH = search ? search.getBoundingClientRect().height : 0
      shell.style.setProperty(
        '--flat-outliner-offset',
        `${headerH + searchH}px`
      )
    }

    writeTreeHeight()
    const observer = new ResizeObserver(writeTreeHeight)
    observer.observe(header)
    const search = header.querySelector('.maya-outliner-search-wrap')
    if (search) observer.observe(search)
    window.addEventListener('resize', writeTreeHeight)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', writeTreeHeight)
    }
  }, [drawerOpen])

  useEffect(() => {
    document.body.classList.toggle('flat-drawer-open', drawerOpen)
    return () => document.body.classList.remove('flat-drawer-open')
  }, [drawerOpen])

  const handleToggleDrawer = () => {
    setDrawerOpen((open) => !open)
  }

  const sceneName = sceneNameFromPath(location.pathname)
  const skipHref =
    location.pathname !== '/about' && !location.pathname.endsWith('/details')
      ? '#hub-active-slide'
      : '#main'

  return (
    <div ref={shellRef} className="flat-shell">
      <a className="skip-link" href={skipHref}>Skip to content</a>
      <WelcomeScreen variant="flat" />
      <header ref={headerRef} className="flat-header">
        <div className="flat-titlebar">
          <MayaLogo />
          <p>
            {sceneName}* - Autotable Daya 2026: {sceneName}
          </p>
        </div>
        <MayaMenubar variant="flat" />
        <div className="flat-tabbar">
          <button
            type="button"
            className="maya-outliner-tab"
            aria-expanded={drawerOpen}
            aria-controls="flat-drawer"
            aria-label={drawerOpen ? 'Close outliner' : 'Open outliner'}
            onClick={handleToggleDrawer}
          >
            Outliner
            <span className="flat-menu-glyph" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
          </button>
        </div>
        <div className="flat-tabbar-edge" aria-hidden="true" />
        <SidebarOutliner
          showCharacters={false}
          drawerOpen={drawerOpen}
          onToggleDrawer={handleToggleDrawer}
          drawerId="flat-drawer"
        />
      </header>
      <main id="main" className="flat-main">
        <Outlet />
      </main>
    </div>
  )
}

export default FlatShell
